/* eslint-disable */
import React, { useMemo, useState } from "react";
import {
  createStyles,
  Card,
  Group,
  Text,
  Select,
  TextInput,
  Table,
  Avatar,
  ActionIcon,
  Pagination,
  Checkbox,
  Badge,
  Center,
} from "@mantine/core";
import { DateRangePicker } from "@mantine/dates";
import {
  IconSearch,
  IconChevronDown,
  IconPencil,
  IconTrash,
  IconShield,
} from "@tabler/icons-react";
import EditUserModal from "../edit";
import DeleteUser from "../delete/deleteUser";
import { useQueryClient } from "@tanstack/react-query";
import Loader from "components/Loader";
import { useHasPermission } from "api/permission/useHasPermission";
import { canManageRole } from "utils/roleHierarchy";

const useStyles = createStyles((theme) => ({
  card: {
    width: "100%",
    padding: 18,
    boxShadow: theme.shadows.sm,
    borderRadius: theme.radius.md,
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 12,
  },
  titleBlock: { display: "flex", flexDirection: "column" },
  title: { fontSize: 18, fontWeight: 700, marginBottom: 4 },
  subtitle: { fontSize: 12, color: theme.colors.gray[6] },
  controls: {
    display: "flex",
    gap: theme.spacing.sm,
    alignItems: "center",
    flexWrap: "wrap",
  },
  tableWrap: { marginTop: 10 },
  thead: { backgroundColor: "#f5f6f7" },
  employeeCell: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.sm,
  },
  empMeta: { lineHeight: 1 },
  empName: { fontWeight: 600, fontSize: 13 },
  empEmail: { fontSize: 13, color: theme.colors.gray[6] },
  roleBadge: {
    borderRadius: 8,
    padding: "4px 8px",
    fontSize: 13,
    fontWeight: 600,
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 14,
  },
  footerText: { fontSize: 13, color: theme.colors.gray[6] },
  actions: {
    display: "flex",
    gap: theme.spacing.xs,
    justifyContent: "flex-start",
  },
  rowsSelect: { width: 64 },
}));

// Helper function to get role badge color
const getRoleBadgeColor = (role: string): string => {
  const colorMap: Record<string, string> = {
    superadmin: "red",
    admin: "blue",
    supervisor: "grape",
    member: "gray",
    director: "cyan",
    band_administrator: "teal",
  };
  return colorMap[role?.toLowerCase()] || "gray";
};

export default function UsersListTable() {
  const queryClient = useQueryClient();
  const { classes } = useStyles();
  const { has: hasEditPermission } = useHasPermission(
    "permission_management.edit_users_permissions"
  );
  const userAndPermissionList: any = queryClient.getQueryData([
    "all-user-permissions",
  ]);
  const currentUser: any = queryClient.getQueryData(["auth-user"]);

  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const [opened, setOpened] = useState(false);
  const [deleteOpen, setOpenDelete] = useState(false);
  const [roleFilter, setRoleFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<string>("last7");
  const [search, setSearch] = useState<string>("");
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [page, setPage] = useState<number>(1);

  // ✅ Extract real API data safely
  const users = userAndPermissionList?.data || [];

  // ✅ Loading state
  if (!userAndPermissionList) {
    return (
      <Card className={classes.card}>
        <Center style={{ height: 200 }}>
          <Loader />
        </Center>
      </Card>
    );
  }

  // ✅ Filter and search logic
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u: any) => {
      const role = u?.role?.toLowerCase() || "";
      const email = u?.userId?.email?.toLowerCase() || "";
      const name = `${u?.userId?.firstName || ""} ${
        u?.userId?.lastName || ""
      }`.toLowerCase();

      // Only show users that current user can manage (role hierarchy check)
      // if (!canManageRole(currentUser?.role, u?.role)) {
      //   return false;
      // }

      if (roleFilter !== "all" && role !== roleFilter) return false;
      if (!q) return true;
      return name.includes(q) || email.includes(q);
    });
  }, [search, roleFilter, statusFilter, users, currentUser?.role]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const startIndex = (page - 1) * rowsPerPage;
  const visible = filtered.slice(startIndex, startIndex + rowsPerPage);

  const statusToBadgeColor = (status: string) => {
    switch (status) {
      case "active":
        return "green";
      case "invited":
        return "violet";
      case "disabled":
        return "gray";
      default:
        return "blue";
    }
  };

  // Count active permissions (boolean true values)
  const countActivePermissions = (permissions: any): number => {
    if (!permissions || typeof permissions !== "object") return 0;

    let count = 0;
    const countRecursive = (obj: any) => {
      for (const key in obj) {
        if (typeof obj[key] === "boolean" && obj[key] === true) {
          count++;
        } else if (typeof obj[key] === "object" && obj[key] !== null) {
          countRecursive(obj[key]);
        }
      }
    };

    countRecursive(permissions);
    return count;
  };

  return (
    <Card className={classes.card}>
      {/* Search and filters */}
      <Group position="apart" mb="md">
        <Group spacing={"xs"}>
          {" "}
          <Text className={classes.title}>Users List</Text>
          <Text className={classes.subtitle}>
            Showing {filtered.length} of {users.length} entries
          </Text>
        </Group>

        <TextInput
          placeholder="Search by name or email"
          icon={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          style={{ width: 250 }}
        />
      </Group>

      {/* Table */}
      <div className={classes.tableWrap}>
        <Table verticalSpacing="sm" highlightOnHover>
          <thead className={classes.thead}>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Permissions</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr>
                <td colSpan={6}>
                  <Center>No users found.</Center>
                </td>
              </tr>
            ) : (
              visible.map((u: any) => {
                const name = `${u?.userId?.firstName || ""} ${
                  u?.userId?.lastName || ""
                }`;
                const initials = name
                  .split(" ")
                  .map((p) => p[0])
                  .slice(0, 2)
                  .join("")
                  .toUpperCase();

                // Check if current user can manage this user's role based on hierarchy
                const canManageUser = canManageRole(currentUser?.role, u?.role);
                const canEdit = canManageUser && hasEditPermission;

                return (
                  <tr key={u._id}>
                    <td>
                      <div className={classes.employeeCell}>
                        <Avatar color="gray" radius={40} size={40}>
                          {initials}
                        </Avatar>
                        <div className={classes.empMeta}>
                          <div className={classes.empName}>{name}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <Text size="sm" className={classes.empEmail}>
                        {u?.userId?.email}
                      </Text>
                    </td>
                    <td>
                      <Badge
                        color={getRoleBadgeColor(u?.role)}
                        variant="light"
                      >
                        {u?.role || "N/A"}
                      </Badge>
                    </td>
                    <td>
                      <Badge color="blue" variant="outline">
                        {countActivePermissions(u?.permissions)} permissions
                      </Badge>
                    </td>
                    <td>
                      <div className={classes.actions}>
                        {/* <ActionIcon variant="light" title="Permissions">
                          <IconShield size={16} />
                        </ActionIcon> */}
                        <ActionIcon
                          variant="light"
                          title={
                            !canManageUser
                              ? `You cannot manage users with role "${u?.role}"`
                              : !hasEditPermission
                              ? "You don't have permission to edit user permissions"
                              : "Edit"
                          }
                          disabled={!canEdit}
                          onClick={() => {
                            if (canEdit) {
                              setSelectedUser(u);
                              setOpened(true);
                            }
                          }}
                        >
                          <IconPencil size={16} />
                        </ActionIcon>
                        {/* <ActionIcon
                          variant="light"
                          color="red"
                          title="Delete"
                          onClick={() => setOpenDelete(true)}
                        >
                          <IconTrash size={16} />
                        </ActionIcon> */}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>

      {/* Pagination */}
      <div className={classes.footer}>
        <Text className={classes.footerText}>
          Showing {visible.length === 0 ? 0 : startIndex + 1}–
          {Math.min(startIndex + visible.length, filtered.length)} of{" "}
          {filtered.length} entries
        </Text>
        <Pagination
          total={pageCount}
          page={page}
          onChange={setPage}
          siblings={1}
          radius="sm"
        />
      </div>

      {/* Modals */}
      <EditUserModal
        user={selectedUser}
        opened={opened}
        onClose={() => setOpened(false)}
      />
      <DeleteUser
        deleteOpen={deleteOpen}
        onClose={() => setOpenDelete(false)}
      />
    </Card>
  );
}
